import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { Capacitor } from '@capacitor/core';
import { useAuth } from '../contexts/AuthContext';
import { mobilePaymentService } from '../services/mobilePayment';
import { paymentsAPI } from '../services/api';
import { is } from 'date-fns/locale';

const PaymentCallback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, getAuthToken } = useAuth();
  
  const [processing, setProcessing] = useState(true);
  const [paymentResult, setPaymentResult] = useState(null);
  const [error, setError] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState({});
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Conservative mobile detection - prioritize Capacitor detection
    // Only treat as mobile app if it's definitely a Capacitor native app
    const isCapacitorNative = Capacitor.isNativePlatform();
    const userAgent = navigator.userAgent || '';
    
    // Secondary check: mobile user agent with WebView indicator for our specific app
    const isAppWebView = userAgent.includes('MyHomeEntryApp') || 
                        (userAgent.includes('wv') && userAgent.includes('Mobile'));
    
    // Be conservative - only mobile if definitely Capacitor or our specific app
    const isMobileDevice = isCapacitorNative || isAppWebView;
    
    setIsMobile(isMobileDevice);
    
    // Store detection results in DOM for debugging
    document.body.setAttribute('data-mobile-detected', isMobileDevice.toString());
    document.body.setAttribute('data-capacitor-native', isCapacitorNative.toString());
    document.body.setAttribute('data-app-webview', isAppWebView.toString());
    document.body.setAttribute('data-user-agent', userAgent);
  }, []);

  // Extracted payment update function to be used by both web and mobile
  const updatePaymentRecord = async (paymentData) => {
    const { status, orderId, paymentId, flatId, buildingId, transactionId, amount, message } = paymentData;
    
    if (status === 'Success') {
      let bearerToken;
      try {
        // Try localStorage tokens first (for MSG91 OTP login), then fall back to Cognito
        bearerToken = localStorage.getItem('idToken') || localStorage.getItem('accessToken');
        if (!bearerToken) {
          bearerToken = await getAuthToken();
        }
        console.log('Bearer token obtained for payment update');
      } catch (authError) {
        console.error('Failed to get authentication token:', authError);
        throw new Error('Unable to authenticate for payment update. Please log in again.');
      }
      
      // Update the payment record in the database using direct API call with bearer token
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'https://myhomeentry.com/api';
        
        const requestBody = {
          paymentId: paymentId,
          flatId: flatId,
          buildingId: buildingId,
          status: 'COMPLETED',
          amount: parseFloat(amount || 0),
          description: 'CCAvenue payment completed successfully',
          razorpayPaymentId: transactionId, // Use CCAvenue transaction ID as receipt
          razorpayOrderId: orderId,
          ccavenueTransactionId: transactionId,
          ccavenueOrderId: orderId,
          ccavenuePaymentMode: 'CCAvenue',
          ccavenueStatusCode: '200',
          ccavenueStatusMessage: message || 'Payment completed successfully',
          completedAt: new Date().toISOString(),
          year: new Date().getFullYear(),
          month: new Date().getMonth() + 1
        };
        
        console.log('Sending payment update request body:', requestBody);
        
        const updateResponse = await fetch(`${apiUrl}/payments/${paymentId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${bearerToken}`
          },
          body: JSON.stringify(requestBody)
        });
        
        if (!updateResponse.ok) {
          const errorText = await updateResponse.text();
          console.error('Payment update failed:', updateResponse.status, updateResponse.statusText, errorText);
          throw new Error(`Payment update failed: ${updateResponse.status} ${updateResponse.statusText}`);
        }
        
        const responseData = await updateResponse.json();
        console.log('Payment update response:', responseData);
        console.log('Payment record updated successfully');
        
      } catch (updateError) {
        console.error('Failed to update payment record:', updateError);
        throw new Error('Payment was successful, but failed to update records. Please contact support.');
      }
    }
  };

  useEffect(() => {
    // Add detection info to body for debugging
    document.body.setAttribute('data-callback-loaded', new Date().toISOString());
    document.body.setAttribute('data-user-authenticated', (!!user).toString());
    document.body.setAttribute('data-search-params', location.search);
    document.body.setAttribute('data-mobile-app', isMobile.toString());
    
    // Real-time mobile detection for immediate processing
    const isCapacitorNative = Capacitor.isNativePlatform();
    const userAgent = navigator.userAgent || '';
    const isAppWebView = userAgent.includes('MyHomeEntryApp') || 
                        (userAgent.includes('wv') && userAgent.includes('Mobile'));
    const isMobileDevice = isCapacitorNative || isAppWebView;
    
    // For mobile apps - use real-time detection instead of state
    if (isMobileDevice) {
      // Mobile flow started
      
      document.body.setAttribute('data-mobile-flow', 'true');
      
      // Close any remaining WebView first
      if (Capacitor.isNativePlatform()) {
        import('@capacitor/browser').then(({ Browser }) => {
          Browser.close().catch(() => {}); // Silent fail
        }).catch(() => {});
      }
      
      // Priority 1: Check URL parameters first (CCAvenue callback redirect)
      const urlParams = new URLSearchParams(window.location.search);
      const hasUrlParams = urlParams.size > 0;

      if (hasUrlParams && window.location.pathname === '/payment-callback') {
        document.body.setAttribute('data-mobile-source', 'ccavenue-callback-redirect');
        
        const processUrlParams = async () => {
          try {
            const responseData = Object.fromEntries(urlParams.entries());
            
            // Get stored payment data for database update
            const pendingPaymentData = localStorage.getItem('pendingPayment');
            let originalPaymentData = {};
            
            if (pendingPaymentData) {
              try {
                originalPaymentData = JSON.parse(pendingPaymentData);
                console.log('Retrieved stored payment data:', originalPaymentData);
              } catch (parseError) {
                console.error('Failed to parse stored payment data:', parseError);
              }
            }
            
            // Transform URL parameters into expected format for mobile display
            const paymentStatus = responseData.status || responseData.order_status;
            const displayData = {
              order_status: paymentStatus,
              order_id: responseData.orderId || responseData.order_id || 'N/A',
              tracking_id: responseData.transactionId || responseData.tracking_id || 'N/A',
              amount: responseData.amount || 'N/A',
              status_message: responseData.message || responseData.status_message || (paymentStatus === 'Success' ? 'Payment completed successfully' : 'Payment failed')
            };
            
            setPaymentDetails(displayData);
            
            if (paymentStatus === 'Success') {
              setPaymentResult('success');
              
              // **FIX: Actually update the payment record in database**
              try {
                // Ensure we have a valid amount - try multiple fields
                const paymentAmount = responseData.amount || 
                                    originalPaymentData.amount || 
                                    originalPaymentData.totalAmount || 
                                    originalPaymentData.paidAmount ||
                                    originalPaymentData.outstandingBalance ||
                                    1700; // Fallback for debugging
                
                if (!paymentAmount || paymentAmount <= 0) {
                  throw new Error(`Payment amount not found or invalid: ${paymentAmount}`);
                }
                
                await updatePaymentRecord({
                  status: paymentStatus,
                  orderId: responseData.orderId || responseData.order_id,
                  paymentId: responseData.paymentId || originalPaymentData.paymentId || originalPaymentData.ccavenueOrderId,
                  flatId: responseData.flatId || originalPaymentData.flatId,
                  buildingId: responseData.buildingId || originalPaymentData.buildingId || originalPaymentData.buildingID,
                  transactionId: responseData.transactionId || responseData.tracking_id,
                  amount: paymentAmount,
                  message: responseData.message || responseData.status_message
                });
                
                console.log('Mobile payment record updated successfully');
                setProcessing(false);
                localStorage.removeItem('pendingPayment');
                document.body.setAttribute('data-mobile-process', 'callback-redirect-success-updated');
                
              } catch (updateError) {
                console.error('Failed to update mobile payment record:', updateError);
                setError('Payment successful but failed to update records. Please contact support.');
                setProcessing(false);
                document.body.setAttribute('data-mobile-process', 'callback-redirect-success-update-failed');
              }
              
              // Clean up mobile payment service after successful processing
              if (mobilePaymentService && mobilePaymentService.cancelPayment) {
                mobilePaymentService.cancelPayment();
              }
              
            } else {
              setPaymentResult('failed');
              setError(responseData.failure_message || responseData.status_message || responseData.message || 'Payment failed');
              setProcessing(false);
              localStorage.removeItem('pendingPayment'); // Clean up any pending payment data
              document.body.setAttribute('data-mobile-process', 'callback-redirect-failed');
              
              // Clean up mobile payment service after failed payment
              if (mobilePaymentService) {
                mobilePaymentService.cancelPayment();
              }
            }
            
            return; // Exit mobile flow - we're done
          } catch (urlParseError) {
            console.error('Error processing CCAvenue callback URL parameters:', urlParseError);
            document.body.setAttribute('data-mobile-error', 'callback-url-params-parse-failed');
          }
        };
        
        processUrlParams();
        return;
      }
      
      // Priority 2: Check localStorage for stored results (deep link or direct app launch)
      const storedResult = localStorage.getItem('mobilePaymentResult');

      if (storedResult) {
        document.body.setAttribute('data-mobile-source', 'localStorage-result');
        try {
          const parsedResult = JSON.parse(storedResult);
          localStorage.removeItem('mobilePaymentResult');
          
          // Process stored result from deep link
          const responseData = {
            order_status: parsedResult.status,
            order_id: parsedResult.orderId,
            tracking_id: parsedResult.transactionId,
            amount: parsedResult.amount,
            status_message: parsedResult.message || (parsedResult.status === 'Success' ? 'Payment completed successfully' : 'Payment failed')
          };
          
          setPaymentDetails(responseData);
          
          if (parsedResult.status === 'Success') {
            (async () => {
              try {
                await updatePaymentRecord(parsedResult);
                setPaymentResult('success');
                setProcessing(false);
                document.body.setAttribute('data-mobile-process', 'localStorage-success');
                
                // Clean up mobile payment service after successful processing
                if (mobilePaymentService) {
                  mobilePaymentService.cancelPayment();
                }
              } catch (updateError) {
                setError('Payment was successful, but failed to update records. Please contact support.');
                setPaymentResult('success');
                setProcessing(false);
                document.body.setAttribute('data-mobile-process', 'localStorage-update-failed');
                
                // Clean up mobile payment service even if update failed
                if (mobilePaymentService) {
                  mobilePaymentService.cancelPayment();
                }
              }
            })();
          } else {
            setPaymentResult('failed');
            setError(parsedResult.message || 'Payment failed');
            setProcessing(false);
            document.body.setAttribute('data-mobile-process', 'localStorage-failed');
            
            // Clean up mobile payment service after failed payment
            if (mobilePaymentService) {
              mobilePaymentService.cancelPayment();
            }
          }
          return;
        } catch (parseError) {
          document.body.setAttribute('data-mobile-error', 'localStorage-parse-failed');
        }
      }
      
      // Priority 3: Check if we have a pending payment but no URL params or stored result
      // This would be unusual - typically CCAvenue redirects with params or app stores result
      const pendingPayment = localStorage.getItem('pendingPayment');

      if (pendingPayment && window.location.pathname === '/payment-callback') {
        document.body.setAttribute('data-mobile-source', 'pending-payment-fallback');
        
        
        // Clean up and show error
        setError('Payment callback data was not received. Please check your payment history or contact support if amount was debited.');
        setPaymentResult('failed');
        setProcessing(false);
        localStorage.removeItem('pendingPayment');
        document.body.setAttribute('data-mobile-process', 'no-callback-data-error');
        
        // Clean up mobile payment service
        if (mobilePaymentService) {
          mobilePaymentService.cancelPayment();
        }
        
        return;
      }
    }
  }, []);

  // Regular payment callback processing (for web and when URL has response data)
  useEffect(() => {
    // Skip web flow for mobile devices - they have their own handling logic above
    if (Capacitor.isNativePlatform()) {
      return; // Skip web flow entirely for mobile devices
    }
    
    if (paymentResult || processing === false) {
      return; // Skip if already processed
    }

    // Only process if we're not waiting for mobile payment completion
    const pendingPayment = localStorage.getItem('pendingPayment');
    
    if (pendingPayment && window.location.pathname === '/payment-callback') {
      // Let mobile handling in the above useEffect take care of this
      return;
    }

    const handlePaymentResponse = async () => {
      const params = new URLSearchParams(window.location.search);
      
      if (params.size === 0) {
        setError('No payment response received');
        setPaymentResult('failed');
        setProcessing(false);
        return;
      }

      const responseData = Object.fromEntries(params.entries());
      
      // Transform URL parameters into expected format for display
      const paymentStatus = responseData.status || responseData.order_status;
      const displayData = {
        order_status: paymentStatus,
        order_id: responseData.orderId || responseData.order_id || 'N/A',
        tracking_id: responseData.transactionId || responseData.tracking_id || 'N/A',
        amount: responseData.amount || 'N/A',
        status_message: responseData.message || responseData.status_message || (paymentStatus === 'Success' ? 'Payment completed successfully' : 'Payment failed')
      };
      
      setPaymentDetails(displayData);

      // Check for success using either 'status' or 'order_status' parameter
      if (paymentStatus === 'Success') {
        setPaymentResult('success');
        try {
          // Prepare payment update data with all required fields
          const updateData = {
            status: 'Success',
            orderId: responseData.orderId || responseData.order_id,
            paymentId: responseData.paymentId || responseData.payment_id,
            flatId: responseData.flatId || responseData.flat_id,
            buildingId: responseData.buildingId || responseData.building_id,
            transactionId: responseData.transactionId || responseData.tracking_id,
            amount: responseData.amount,
            message: responseData.message || responseData.status_message
          };
          
          // Only call updatePaymentRecord if we have the required IDs
          if (updateData.paymentId && updateData.flatId && updateData.buildingId) {
            await updatePaymentRecord(updateData);
            console.log('Web payment record updated successfully');
          } else {
            console.log('Skipping payment record update - missing required IDs (paymentId, flatId, buildingId)');
            console.log('Available data:', updateData);
          }
        } catch (updateError) {
          console.error('Web payment update failed:', updateError);
          // Continue with success display even if update fails
        }
      } else {
        setPaymentResult('failed');
        setError(responseData.failure_message || responseData.status_message || responseData.message || 'Payment failed');
      }
      setProcessing(false);
    };

    handlePaymentResponse();
  }, [processing, paymentResult]);

  const handleReturnToDashboard = () => {
    // Close popup if opened as popup
    if (window.opener && !window.opener.closed) {
      window.opener.focus();
      window.close();
      return;
    }
    
    // Navigate to dashboard with a success parameter to trigger page refresh
    navigate('/?payment=success', { replace: true });
  };

  const handleTryAgain = () => {
    // Close popup if opened as popup
    if (window.opener && !window.opener.closed) {
      window.opener.focus();
      window.close();
      return;
    }
    
    // Navigate back to dashboard
    navigate('/', { replace: true });
  };

  if (processing) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          p: 2
        }}
      >
        <Paper
          sx={{
            p: 4,
            textAlign: 'center',
            maxWidth: 500,
            width: '100%',
            borderRadius: 3,
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
          }}
        >
          <CircularProgress size={60} sx={{ mb: 3, color: '#667eea' }} />
          <Typography variant="h5" gutterBottom>
            Processing Payment
          </Typography>
          <Typography color="text.secondary">
            Please wait while we verify your payment details...
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (paymentResult === 'success') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          p: 2
        }}
      >
        <Paper
          sx={{
            p: { xs: 2, sm: 4 },
            textAlign: 'center',
            maxWidth: 600,
            width: '100%',
            borderRadius: 3,
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
            mx: 2
          }}
        >
          <CheckCircleIcon 
            sx={{ 
              fontSize: { xs: 60, sm: 80 }, 
              color: 'success.main', 
              mb: 2 
            }} 
          />
          <Typography 
            variant="h4" 
            gutterBottom 
            color="success.main"
            sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
          >
            Payment Successful! 🎉
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            Your payment has been processed successfully.
          </Typography>
          
          <Card sx={{ mb: 3, textAlign: 'left' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Payment Details
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Order ID:</strong>
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      wordBreak: 'break-all',
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      bgcolor: 'grey.100',
                      p: 1,
                      borderRadius: 1
                    }}
                  >
                    {paymentDetails.order_id || 'N/A'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography><strong>Amount:</strong></Typography>
                  <Typography variant="h6" color="success.main">₹{paymentDetails.amount || 'N/A'}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Transaction ID:</strong>
                  </Typography>
                  <Typography 
                    variant="body1"
                    sx={{ 
                      wordBreak: 'break-all',
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      bgcolor: 'grey.100',
                      p: 1,
                      borderRadius: 1
                    }}
                  >
                    {paymentDetails.tracking_id || 'N/A'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography><strong>Status:</strong></Typography>
                  <Chip 
                    label={paymentDetails.order_status || 'Success'} 
                    color="success" 
                    size="small" 
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
          
          <Button
            variant="contained"
            onClick={handleReturnToDashboard}
            fullWidth
            sx={{
              bgcolor: '#667eea',
              '&:hover': { bgcolor: '#5a67d8' },
              px: 4,
              py: 1.5,
              fontSize: { xs: '0.875rem', sm: '1rem' },
              maxWidth: { xs: '100%', sm: '300px' }
            }}
          >
            RETURN TO DASHBOARD
          </Button>
        </Paper>
      </Box>
    );
  }

  // Error or failure state
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        p: 2
      }}
    >
      <Paper
        sx={{
          p: 4,
          textAlign: 'center',
          maxWidth: 600,
          width: '100%',
          borderRadius: 3,
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
        }}
      >
        <ErrorIcon 
          sx={{ 
            fontSize: 80, 
            color: 'error.main', 
            mb: 2 
          }} 
        />
        <Typography variant="h4" gutterBottom color="error.main">
          Payment Failed
        </Typography>
        <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
          {error || 'Your payment could not be processed. Please try again.'}
        </Alert>
        
        {paymentDetails && Object.keys(paymentDetails).length > 0 && (
          <Card sx={{ mb: 3, textAlign: 'left' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Payment Details
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography><strong>Order ID:</strong></Typography>
                  <Typography>{paymentDetails.order_id || 'N/A'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography><strong>Status:</strong></Typography>
                  <Chip 
                    label={paymentDetails.order_status || 'Failed'} 
                    color="error" 
                    size="small" 
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography><strong>Message:</strong></Typography>
                  <Typography>{paymentDetails.status_message || 'Payment failed'}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}
        
        <Button
          variant="contained"
          onClick={handleTryAgain}
          sx={{
            bgcolor: '#667eea',
            '&:hover': { bgcolor: '#5a67d8' },
            px: 4,
            py: 1.5
          }}
        >
          Return to Dashboard
        </Button>
      </Paper>
    </Box>
  );
};

export default PaymentCallback;